import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { OperaContext } from "../../../context/OperaContext";
import { cityOptions, venueOptionsByCity } from "./_components/constants";

function ImportTab() {
  const { fetchAdminData } = useContext(OperaContext);
  const [startYear, setStartYear] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [endYear, setEndYear] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [type, setType] = useState("opera");
  const [cityId, setCityId] = useState("");
  const [venueId, setVenueId] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [cancelBatch, setCancelBatch] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [showExportedEvents, setShowExportedEvents] = useState(false);
  const [activeTab, setActiveTab] = useState("basic"); // 'basic', 'detailed', 'ready'
  const [successMessage, setSuccessMessage] = useState("");

  // Maximum number of events that can be selected at once
  const MAX_SELECTIONS = 5;

  // Categorize events by their stage
  const categorizedEvents = useMemo(() => {
    const basicEvents = events.filter(
      (event) => !event.detailsImported && !event.exported
    );
    const detailedEvents = events.filter(
      (event) => event.detailsImported && !event.exported
    );
    const exportedEvents = events.filter((event) => event.exported);

    return {
      basic: basicEvents,
      detailed: detailedEvents,
      exported: exportedEvents,
    };
  }, [events]);

  // Get the active events to display based on the current tab and settings
  const activeEvents = useMemo(() => {
    switch (activeTab) {
      case "basic":
        return categorizedEvents.basic;
      case "detailed":
        return categorizedEvents.detailed;
      case "exported":
        return showExportedEvents ? categorizedEvents.exported : [];
      default:
        return [];
    }
  }, [activeTab, categorizedEvents, showExportedEvents]);

  // Calculate counts for each category
  const eventCounts = useMemo(
    () => ({
      basic: categorizedEvents.basic.length,
      detailed: categorizedEvents.detailed.length,
      exported: categorizedEvents.exported.length,
    }),
    [categorizedEvents]
  );

  useEffect(() => {
    fetchScrapedEvents();
  }, []);

  const fetchScrapedEvents = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get("/api/scrape/scraped");
      setEvents(res.data || []);
    } catch (err) {
      console.error("Error fetching scraped events:", err);
      setError(
        "Failed to load scraped events: "(
          err.response?.data?.error || err.message
        )
      );
    }
    setLoading(false);
  };

  const handleScrape = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage("");
    try {
      const res = await axios.get("/api/scrape/enhanced", {
        params: {
          startYear,
          startMonth,
          endYear: endYear || undefined,
          endMonth: endMonth || undefined,
          type,
          cityId: cityId || undefined,
          venueId: venueId || undefined,
        },
      });

      if (res.data.error) {
        setError(res.data.error);
      } else if (res.data.success) {
        // Show success message with details
        setError(null);
        setSuccessMessage(res.data.message);
      }
      await fetchScrapedEvents();
    } catch (err) {
      console.error("Error in enhanced scraping:", err);
      setError(
        "Failed to scrape events: " + (err.response?.data?.error || err.message)
      );
    }
    setLoading(false);
  };

  const handleCityChange = (e) => {
    const selectedCityId = e.target.value;
    setCityId(selectedCityId);
    setVenueId(""); // Reset venue selection when city changes
  };

  const handleSelectEvent = (eventId) => {
    setSelectedEvents((prev) => {
      // If already selected, remove it
      if (prev.includes(eventId)) {
        return prev.filter((id) => id !== eventId);
      }

      // If not selected yet, add it only if under the limit
      if (prev.length < MAX_SELECTIONS) {
        return [...prev, eventId];
      }

      // Otherwise keep the selection as is
      return prev;
    });
  };

  // Auto-select up to 5 events in the current active category
  const handleAutoSelect = () => {
    let eventsToSelect;

    switch (activeTab) {
      case "basic":
        eventsToSelect = categorizedEvents.basic.slice(0, MAX_SELECTIONS);
        break;
      case "detailed":
        eventsToSelect = categorizedEvents.detailed.slice();
        break;
      case "exported":
        eventsToSelect = categorizedEvents.exported.slice();
        break;
      default:
        eventsToSelect = [];
    }

    setSelectedEvents(eventsToSelect.map((event) => event._id.toString()));
  };

  const handleImportDetailsBatch = async () => {
    if (selectedEvents.length > 5) {
      setError("Please select up to 5 events for detailed import.");
      return;
    }
    if (
      !window.confirm(`Import details for ${selectedEvents.length} events?`)
    ) {
      return;
    }

    setIsBatchProcessing(true);
    setCancelBatch(false);
    setProgress({ current: 0, total: selectedEvents.length });
    setError(null);

    const errors = [];
    for (let i = 0; i < selectedEvents.length; i++) {
      if (cancelBatch) {
        setError("Batch import canceled.");
        break;
      }

      const eventId = selectedEvents[i];

      setProgress((prev) => ({ ...prev, current: i + 1 }));
      try {
        const res = await axios.post("/api/scrape/details/batch", {
          ids: [eventId],
        });

        if (res.data.errors.length > 0) {
          errors.push(...res.data.errors);
        }
      } catch (err) {
        errors.push(
          `Failed to import details for event ${eventId}: ${err.response?.data?.error || err.message
          }`
        );
      }
    }

    if (errors.length > 0) {
      setError(errors.join("; "));
    }
    setSelectedEvents([]);
    await fetchScrapedEvents();
    setIsBatchProcessing(false);
    setProgress({ current: 0, total: 0 });
  };

  const handleExportBatch = async () => {
    if (
      !window.confirm(`Export ${selectedEvents.length} events to Events tab?`)
    ) {
      return;
    }

    setIsBatchProcessing(true);
    setCancelBatch(false);
    setProgress({ current: 0, total: selectedEvents.length });
    setError(null);

    const errors = [];
    for (let i = 0; i < selectedEvents.length; i++) {
      if (cancelBatch) {
        setError("Batch export canceled.");
        break;
      }

      const eventId = selectedEvents[i];

      setProgress((prev) => ({ ...prev, current: i + 1 }));
      try {
        const res = await axios.post("/api/scrape/export/batch", {
          ids: [eventId],
        });

        if (res.data.errors.length > 0) {
          errors.push(...res.data.errors);
        }
      } catch (err) {
        errors.push(
          `Failed to export event ${eventId}: ${err.response?.data?.error || err.message
          }`
        );
      }
    }

    if (errors.length > 0) {
      setError(errors.join("; "));
    }
    setSelectedEvents([]);
    await fetchScrapedEvents();
    await fetchAdminData();
    setIsBatchProcessing(false);
    setProgress({ current: 0, total: 0 });
  };

  const handleCancelBatch = () => {
    setCancelBatch(true);
  };

  // Handle tab selection
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedEvents([]); // Clear selections when changing tabs
  };

  // Generate the tab class based on active state
  const getTabClass = (tab) => {
    const baseClass = "px-4 py-2 text-center transition-colors";
    const activeClass = "bg-[#d4a017] text-white font-bold";
    const inactiveClass = "bg-[#333] text-gray-300 hover:bg-[#444]";

    return `${baseClass} ${activeTab === tab ? activeClass : inactiveClass}`;
  };

  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white">
      <h2 className="text-xl text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-4">
        Import Events
      </h2>

      {/* Workflow Description */}
      <div className="mb-6 p-4 bg-[#2a2a2a] rounded-lg border border-[#555]">
        <h3 className="text-lg font-semibold mb-2 text-[#d4a017]">
          New Streamlined Workflow
        </h3>
        <div className="text-sm text-gray-300">
          <p className="mb-2">
            <strong>Step 1:</strong> Set your date range, type, city, and venue,
            then click "Scrape & Auto-Import Details"
          </p>
          <p className="mb-2">
            <strong>Step 2:</strong> The system will automatically:
          </p>
          <ul className="list-disc list-inside ml-4 mb-2">
            <li>Scrape basic event information from Bachtrack.com</li>
            <li>Automatically import detailed information for all events</li>
            <li>Process events in batches with appropriate timeouts</li>
          </ul>
          <p>
            <strong>Step 3:</strong> Once complete, events will be ready for
            export in the "Ready to Export" tab
          </p>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap gap-4 mb-4">
          <input
            type="text"
            placeholder="Start Year (e.g., 2025)"
            value={startYear}
            onChange={(e) => setStartYear(e.target.value)}
            className="p-2 bg-[#333] text-white border border-[#555] rounded"
          />
          <select
            value={startMonth}
            onChange={(e) => setStartMonth(e.target.value)}
            className="p-2 bg-[#333] text-white border border-[#555] rounded"
          >
            <option value="">Start Month</option>
            <option value="01">January</option>
            <option value="02">February</option>
            <option value="03">March</option>
            <option value="04">April</option>
            <option value="05">May</option>
            <option value="06">June</option>
            <option value="07">July</option>
            <option value="08">August</option>
            <option value="09">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>
          <input
            type="text"
            placeholder="End Year (e.g., 2025)"
            value={endYear}
            onChange={(e) => setEndYear(e.target.value)}
            className="p-2 bg-[#333] text-white border border-[#555] rounded"
          />
          <select
            value={endMonth}
            onChange={(e) => setEndMonth(e.target.value)}
            className="p-2 bg-[#333] text-white border border-[#555] rounded"
          >
            <option value="">End Month</option>
            <option value="01">January</option>
            <option value="02">February</option>
            <option value="03">March</option>
            <option value="04">April</option>
            <option value="05">May</option>
            <option value="06">June</option>
            <option value="07">July</option>
            <option value="08">August</option>
            <option value="09">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="p-2 bg-[#333] text-white border border-[#555] rounded"
          >
            <option value="opera">Opera</option>
            <option value="concerts">Concerts</option>
            <option value="dance">Dance</option>
          </select>
          <select
            value={cityId}
            onChange={handleCityChange}
            className="p-2 bg-[#333] text-white border border-[#555] rounded"
          >
            {cityOptions.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </select>
          <select
            value={venueId}
            onChange={(e) => setVenueId(e.target.value)}
            className="p-2 bg-[#333] text-white border border-[#555] rounded"
            disabled={!cityId}
          >
            {venueOptionsByCity[cityId].map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleScrape}
            disabled={
              !startYear ||
              !startMonth ||
              !endYear ||
              !endMonth ||
              !cityId ||
              !venueId ||
              loading ||
              isBatchProcessing
            }
            className={`px-4 py-2 rounded text-white transition-colors ${!startYear ||
              !startMonth ||
              !endYear ||
              !endMonth ||
              !cityId ||
              !venueId ||
              loading ||
              isBatchProcessing
              ? "bg-gray-500 cursor-not-allowed"
              : "bg-[#d4a017] hover:bg-[#b38b00]"
              }`}
          >
            {loading ? "Processing..." : "Scrape & Auto-Import Details"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center mb-4">
          <p>Step 1: Scraping basic event information...</p>
          <p className="text-sm text-gray-400">
            Step 2: Auto-importing details will follow...
          </p>
        </div>
      )}
      {error && <p className="text-[#f44336] text-center">{error}</p>}
      {successMessage && (
        <div className="text-center mb-4">
          <p className="text-green-600 font-semibold">{successMessage}</p>
          <p className="text-sm text-gray-400">
            Events are now ready for export in the "Ready to Export" tab
          </p>
        </div>
      )}

      {isBatchProcessing && (
        <div className="text-center mb-4">
          <p>
            Processing {progress.current} of {progress.total}...
          </p>
          <button
            onClick={handleCancelBatch}
            className="mt-2 px-4 py-2 bg-[#f44336] hover:bg-[#da190b] text-white rounded transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex flex-wrap mb-6">
        <div className="flex rounded-md overflow-hidden border border-[#555] mb-4">
          <button
            className={getTabClass("basic")}
            onClick={() => handleTabChange("basic")}
          >
            Basic Info ({eventCounts.basic})
          </button>
          <button
            className={getTabClass("detailed")}
            onClick={() => handleTabChange("detailed")}
          >
            Ready to Export ({eventCounts.detailed})
          </button>
          <button
            className={getTabClass("exported")}
            onClick={() => handleTabChange("exported")}
          >
            Exported ({eventCounts.exported})
          </button>
        </div>
      </div>

      {/* Controls for each view */}
      <div className="flex flex-wrap gap-4 mb-4">
        {activeTab === "basic" && (
          <>
            <button
              onClick={handleImportDetailsBatch}
              disabled={
                selectedEvents.length === 0 || loading || isBatchProcessing
              }
              className={`px-4 py-2 rounded text-white transition-colors ${selectedEvents.length === 0 || loading || isBatchProcessing
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-[#4CAF50] hover:bg-[#45a049]"
                }`}
            >
              Import Details for Selected ({selectedEvents.length})
            </button>
            <button
              onClick={handleAutoSelect}
              disabled={
                loading ||
                isBatchProcessing ||
                categorizedEvents.basic.length === 0
              }
              className={`px-4 py-2 rounded text-white transition-colors ${loading ||
                isBatchProcessing ||
                categorizedEvents.basic.length === 0
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-[#d4a017] hover:bg-[#b38b00]"
                }`}
            >
              Auto-Select{" "}
              {Math.min(MAX_SELECTIONS, categorizedEvents.basic.length)} Events
            </button>
          </>
        )}

        {activeTab === "detailed" && (
          <>
            <button
              onClick={handleExportBatch}
              disabled={
                selectedEvents.length === 0 || loading || isBatchProcessing
              }
              className={`px-4 py-2 rounded text-white transition-colors mr-2 ${selectedEvents.length === 0 || loading || isBatchProcessing
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-[#d4a017] hover:bg-[#b38b00]"
                }`}
            >
              Export Selected to Events Tab ({selectedEvents.length})
            </button>
            <button
              onClick={handleAutoSelect}
              disabled={
                loading ||
                isBatchProcessing ||
                categorizedEvents.detailed.length === 0
              }
              className={`px-4 py-2 rounded text-white transition-colors ${loading ||
                isBatchProcessing ||
                categorizedEvents.detailed.length === 0
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-[#4CAF50] hover:bg-[#45a049]"
                }`}
            >
              Auto-Select all events
            </button>
          </>
        )}

        {activeTab === "exported" && (
          <div className="flex items-center">
            <input
              type="checkbox"
              id="show-exported"
              checked={showExportedEvents}
              onChange={(e) => setShowExportedEvents(e.target.checked)}
              className="mr-2 h-4 w-4"
            />
            <label htmlFor="show-exported">Show exported events</label>

            {showExportedEvents && categorizedEvents.exported.length > 0 && (
              <button
                onClick={handleAutoSelect}
                disabled={loading || isBatchProcessing}
                className="ml-4 px-4 py-2 rounded text-white transition-colors bg-[#4CAF50] hover:bg-[#45a049]"
              >
                Auto-Select{" "}
                {Math.min(MAX_SELECTIONS, categorizedEvents.exported.length)}{" "}
                Events
              </button>
            )}
          </div>
        )}
      </div>

      {selectedEvents.length >= MAX_SELECTIONS && activeTab === "basic" && (
        <div className="text-amber-400 text-sm mb-4">
          Maximum selection limit reached ({MAX_SELECTIONS} events)
        </div>
      )}

      {/* List of events */}
      {activeTab === "exported" && !showExportedEvents ? (
        <div className="text-center text-gray-400">
          <p>Toggle the checkbox above to view exported events.</p>
        </div>
      ) : (
        <div className="max-h-[600px] overflow-y-auto">
          {activeEvents.length === 0 ? (
            <p className="text-center text-gray-400">
              No events in this category.
            </p>
          ) : (
            <ul className="space-y-6">
              {activeEvents.map((event) => (
                <li
                  key={event._id}
                  className="border border-gray-300 p-4 rounded shadow"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {activeTab !== "exported" || showExportedEvents ? (
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(event._id.toString())}
                        onChange={() => handleSelectEvent(event._id.toString())}
                        disabled={
                          loading ||
                          isBatchProcessing ||
                          (selectedEvents.length >= MAX_SELECTIONS &&
                            !selectedEvents.includes(event._id.toString()))
                        }
                        className="h-4 w-4 text-[#d4a017] bg-[#333] border-[#555] rounded"
                      />
                    ) : null}
                    <h3 className="text-xl font-semibold text-[#d4a017]">
                      {event.title}
                    </h3>
                  </div>
                  <p>
                    <strong>Type:</strong> {event.type}
                  </p>
                  <p>
                    <strong>Genre:</strong> {event.genre}
                  </p>
                  <p>
                    <strong>Date:</strong> {event.date || "Not specified"}
                  </p>
                  <p>
                    <strong>Venue:</strong> {event.venue},{" "}
                    {event.city || "Unknown"}
                    {event.country ? `, ${event.country}` : ""}
                  </p>
                  <p>
                    <strong>Composer:</strong>{" "}
                    {event.composer || "Not specified"}
                  </p>
                  <p>
                    <strong>Performers:</strong>{" "}
                    {event.performers.length > 0
                      ? event.performers.join(", ")
                      : "Not specified"}
                  </p>
                  {event.crews.length > 0 && (
                    <p>
                      <strong>Crews:</strong>{" "}
                      {event.crews
                        .map((crew) => `${crew.role}: ${crew.name}`)
                        .join(", ")}
                    </p>
                  )}
                  {event.ensamble.Orchester || event.ensamble.Chor ? (
                    <p>
                      <strong>Ensamble:</strong>
                      {event.ensamble.Orchester &&
                        ` Orchester: ${event.ensamble.Orchester}`}
                      {event.ensamble.Chor && `, Chor: ${event.ensamble.Chor}`}
                    </p>
                  ) : null}
                  {event.tags && event.tags.length > 0 && (
                    <p>
                      <strong>Tags:</strong> {event.tags.join(", ")}
                    </p>
                  )}
                  <div className="mt-2 text-sm text-gray-400">
                    <p>
                      Details Imported: {event.detailsImported ? "Yes" : "No"}
                    </p>
                    {activeTab === "exported" && <p>Exported: Yes</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default ImportTab;
